import { StatsigClient } from '@statsig/js-client';
import { StatsigSessionReplayPlugin } from '@statsig/session-replay';
import { StatsigAutoCapturePlugin } from '@statsig/web-analytics';

// Helper: determine if the provided (or current) user is an internal admin we wish to exclude from Statsig.
function isInternalAdmin(user = {}) {
  const email = typeof user.email === 'string' ? user.email : '';
  return email.endsWith('@avidai.com');
}

/**
 * Session replay (rrweb) can throw SecurityError when accessing window.removeEventListener
 * in restricted contexts (e.g. cross-origin iframe, strict CSP). Only enable when safe.
 */
function canUseSessionReplay() {
  try {
    if (typeof window === 'undefined' || !window.removeEventListener) {
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

// Create a singleton instance of the Statsig client
let statsigClient = null;
let initializationPromise = null;

export default {
  /**
   * Initialize the Statsig client
   * @param {Object} user - The user object to identify the current user
   * @returns {Promise} - A promise that resolves when Statsig is initialized
   */
  async initialize(user = {}) {
    // If already initializing, return the existing promise
    if (initializationPromise) {
      return initializationPromise;
    }

    // If already initialized, return the client
    if (statsigClient) {
      return statsigClient;
    }

    const isInternal = isInternalAdmin(user);

    initializationPromise = (async () => {
      try {
        // Use the user ID from the user object or generate a random one
        const userID = user.id || `anonymous-${Math.random().toString(36).substring(2, 15)}`;

        // Create a clean user object with only the properties we want to send to Statsig
        const statsigUser = {
          userID,
          customIDs: {
            ...(user.agency_id && { agencyId: user.agency_id }),
            churn_zero: user.agency_id || (window.client ? window.client.url : null),
          },
          // User information
          custom:{
            ...(user.name && { userName: user.name }),
            ...(user.user_type && { userType: user.user_type }),
            ...(user.organization && { organization: user.organization }),
            ...(isInternal && { internal_user: true }),
          },
          ...(user.email && { email: user.email }),
        };

        // Add client information if available
        if (window.client) {
          // Client information
          statsigUser.customIDs.clientId = window.client.id;
          statsigUser.custom.clientName = window.client.name;
          statsigUser.custom.clientUrl = window.client.url;
          statsigUser.custom.clientPlanLevel = window.client.plan_level;
        }

        var env = 'development';
        if(['staging','production'].includes(window.app_env)) {
          env = window.app_env;
        }

        const plugins = [
          ...(canUseSessionReplay() ? [new StatsigSessionReplayPlugin()] : []),
          new StatsigAutoCapturePlugin(),
        ];

        statsigClient = new StatsigClient(
          "client-Pf3AuYbu7eAbn0YWdSAaid1erjKTjX5y0kP70akzk1d", // Your Statsig client key
          statsigUser,
          {
            environment: { tier: env },
            plugins,
          }
        );

        await statsigClient.initializeAsync();

        // Expose the Statsig client to the window object
        window.Statsig = statsigClient;

        return statsigClient;
      } catch (error) {
        const isSecurityError = error?.name === 'SecurityError' || error?.message?.includes('security policy');
        if (isSecurityError && plugins?.some((p) => p?.constructor?.name === 'StatsigSessionReplayPlugin')) {
          try {
            statsigClient = new StatsigClient(
              "client-Pf3AuYbu7eAbn0YWdSAaid1erjKTjX5y0kP70akzk1d",
              statsigUser,
              {
                environment: { tier: env },
                plugins: [new StatsigAutoCapturePlugin()],
              }
            );
            await statsigClient.initializeAsync();
            window.Statsig = statsigClient;
            return statsigClient;
          } catch (fallbackError) {
            console.error('Failed to initialize Statsig (without session replay):', fallbackError);
            throw fallbackError;
          }
        }
        console.error('Failed to initialize Statsig:', error);
        throw error;
      } finally {
        initializationPromise = null;
      }
    })();

    return initializationPromise;
  },

  /**
   * Update the Statsig user
   * @param {Object} user - The updated user object
   * @returns {Promise} - A promise that resolves when the user is updated
   */
  async updateUser(user = {}) {
    const isInternal = isInternalAdmin(user);

    if (!statsigClient) {
      return this.initialize(user);
    }

    // Create a clean user object with only the properties we want to send to Statsig
    const statsigUser = {
      userID: user.id || statsigClient.getStableID(),
      customIDs: {
        ...(user.agency_id && { agencyId: user.agency_id }),
        churn_zero: user.agency_id || (window.client ? window.client.url : null),
      },
      // User information
      custom:{
        ...(user.name && { userName: user.name }),
        ...(user.user_type && { userType: user.user_type }),
        ...(user.organization && { organization: user.organization }),
        ...(isInternal && { internal_user: true }),
      },
      ...(user.email && { email: user.email }),
    };

    // Add client information if available
    if (window.client) {
      // Client information
      statsigUser.customIDs.clientId = window.client.id;
      statsigUser.custom.clientName = window.client.name;
      statsigUser.custom.clientUrl = window.client.url;
      statsigUser.custom.clientPlanLevel = window.client.plan_level;
    }

    await statsigClient.updateUser(statsigUser);
    return statsigClient;
  },

  /**
   * Check if a feature gate is enabled
   * @param {string} gateName - The name of the feature gate to check
   * @returns {boolean} - Whether the feature gate is enabled
   */
  checkGate(gateName) {
    if (!statsigClient) {
      return false;
    }

    return statsigClient.checkGate(gateName);
  },

  /**
   * Get a dynamic config
   * @param {string} configName - The name of the config to get
   * @returns {Object} - The dynamic config
   */
  getConfig(configName) {
    if (!statsigClient) {
      return {};
    }

    return statsigClient.getConfig(configName);
  },

  /**
   * Log an event to Statsig
   * @param {string} eventName - The name of the event to log
   * @param {Object} value - The value to log with the event
   * @param {Object} metadata - Additional metadata to log with the event
   */
  logEvent(eventName, value = null, metadata = {}) {
    if (!statsigClient) {
      return;
    }

    statsigClient.logEvent(eventName, value, metadata);
  },

  /**
   * Get the Statsig client instance
   * @returns {StatsigClient} - The Statsig client instance
   */
  getClient() {
    return statsigClient;
  }
};
